import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useContent } from "../lib/ContentContext";

export default function Handovers() {
  const { handovers, t } = useContent();
  const [idx, setIdx] = useState(0);

  // Show 3 photos per "page" on desktop, 1 on mobile (handled by CSS grid)
  const VISIBLE_DESKTOP = 3;
  const totalPages = Math.max(1, Math.ceil(handovers.length / VISIBLE_DESKTOP));

  useEffect(() => {
    if (totalPages <= 1) return;
    const timer = setInterval(() => setIdx((i) => (i + 1) % totalPages), 6500);
    return () => clearInterval(timer);
  }, [totalPages]);

  if (!handovers.length) return null;

  const eyebrow = t("handovers.eyebrow", "Foto Serah Terima Unit");
  const heading = t("handovers.heading", "Momen kebanggaan\npelanggan kami.");
  const subheading = t(
    "handovers.subheading",
    "Dokumentasi penyerahan unit baru BYD dari showroom Haka Auto Cibubur langsung ke rumah pelanggan."
  );

  const prev = () => setIdx((i) => (i - 1 + totalPages) % totalPages);
  const next = () => setIdx((i) => (i + 1) % totalPages);

  // Slice the visible window
  const start = idx * VISIBLE_DESKTOP;
  const visible = handovers.slice(start, start + VISIBLE_DESKTOP);
  // Pad with first items so grid stays full on the last page
  while (visible.length < VISIBLE_DESKTOP && handovers.length >= VISIBLE_DESKTOP) {
    visible.push(handovers[(start + visible.length) % handovers.length]);
  }

  return (
    <section
      className="bg-white border-t border-[#E5E5EA]"
      data-testid="handovers-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32">
        <div className="max-w-2xl mb-12 lg:mb-16">
          <p className="text-xs uppercase tracking-[0.25em] text-[#6E6E73] mb-5">
            {eyebrow}
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-[#0F0F0F] leading-[1.1] whitespace-pre-line">
            {heading}
          </h2>
          <p className="mt-5 text-base text-[#6E6E73] leading-relaxed">
            {subheading}
          </p>
        </div>

        {/* Portrait grid carousel — 3 photos per page on desktop, scroll snap on mobile */}
        <div className="relative">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
            {visible.map((h, i) => (
              <figure
                key={`${h.id}-${start + i}`}
                className="relative aspect-[4/5] rounded-3xl overflow-hidden bg-[#F5F5F7] border border-[#E5E5EA] group"
                data-testid={`handover-slide-${start + i}`}
              >
                <img
                  src={h.image_url}
                  alt={h.customer_name ? `${h.customer_name} - ${h.unit_name}` : "Foto serah terima BYD"}
                  loading={i === 0 ? "eager" : "lazy"}
                  decoding="async"
                  width="800"
                  height="1000"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                {(h.customer_name || h.unit_name || h.location) && (
                  <figcaption className="absolute inset-x-0 bottom-0 p-5 lg:p-6 bg-gradient-to-t from-black/80 via-black/30 to-transparent text-white">
                    {h.unit_name && (
                      <p className="text-[10px] uppercase tracking-[0.2em] text-white/70 mb-1">
                        {h.unit_name}
                      </p>
                    )}
                    {h.customer_name && (
                      <p className="text-base font-medium">{h.customer_name}</p>
                    )}
                    {(h.location || h.date_label) && (
                      <p className="text-xs text-white/70 mt-0.5">
                        {[h.location, h.date_label].filter(Boolean).join(" · ")}
                      </p>
                    )}
                  </figcaption>
                )}
              </figure>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="mt-8 flex items-center justify-between">
              <div className="flex items-center gap-2">
                {Array.from({ length: totalPages }).map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setIdx(i)}
                    aria-label={`Halaman ${i + 1}`}
                    data-testid={`handover-dot-${i}`}
                    className={`h-1.5 rounded-full transition-all ${
                      i === idx ? "w-8 bg-[#0F0F0F]" : "w-1.5 bg-[#E5E5EA]"
                    }`}
                  />
                ))}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-[#86868B] mr-3 tabular-nums">
                  {idx + 1} / {totalPages}
                </span>
                <button
                  onClick={prev}
                  aria-label="Sebelumnya"
                  data-testid="handover-prev"
                  className="w-10 h-10 rounded-full border border-[#E5E5EA] hover:bg-[#F5F5F7] flex items-center justify-center transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={next}
                  aria-label="Berikutnya"
                  data-testid="handover-next"
                  className="w-10 h-10 rounded-full border border-[#E5E5EA] hover:bg-[#F5F5F7] flex items-center justify-center transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
